import React from "react";
import Layout from "@/components/Layout";
import ClientsMarquee from "@/components/ClientsMarquee";
import GradientStrip from "@/components/GradientStrip";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Reusable layout for service / expertise pages.
 * Props:
 *   - eyebrow, title, lede, image
 *   - sections: [{title, body}]
 *   - bullets: [string]
 *   - formats: [{title, desc}]
 *   - testIdPrefix: string
 */
export default function ServicePageTemplate({
  eyebrow,
  title,
  lede,
  image,
  badge,
  sections = [],
  bullets = [],
  formats = [],
  testIdPrefix = "service",
  accentColor = "#1C1580",
  extraCta,
  afterSections,
}) {
  const t = useT();
  return (
    <Layout headerVariant="dark">
      {/* HERO */}
      <section data-testid={`${testIdPrefix}-hero`} className="relative min-h-[80svh] grain overflow-hidden">
        <div className="absolute inset-0">
          {image && <img src={image} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-deep/85 via-brand-deep/75 to-brand/70" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-52 pb-24">
          <p className="section-num text-accent-pink fade-up">{eyebrow}</p>
          <h1 className="display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight mt-8 max-w-5xl fade-up-2">
            {title}
          </h1>
          {lede && (
            <p className="text-white/85 text-lg md:text-xl leading-relaxed mt-10 max-w-3xl fade-up-3">
              {lede}
            </p>
          )}
          {extraCta && (
            <div className="mt-12 fade-up-3">
              {extraCta}
            </div>
          )}
          {badge && (
            <div className="absolute right-6 md:right-12 lg:right-16 bottom-24 hidden md:block fade-up-4">
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-full border border-white/20">
                <img
                  src={badge.src}
                  alt={badge.alt}
                  className="w-36 h-36 lg:w-44 lg:h-44 rounded-full object-contain"
                  data-testid={`${testIdPrefix}-badge`}
                />
              </div>
            </div>
          )}
        </div>
        <GradientStrip />
      </section>

      {/* SECTIONS */}
      {sections.length > 0 && (
        <section className="bg-paper py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 space-y-20 md:space-y-32">
            {sections.map((s, i) => (
              <div key={i} data-testid={`${testIdPrefix}-section-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-4">
                  <div className="h-px w-12 bg-accent-pink mb-6" />
                  <h2 className="display text-3xl md:text-5xl leading-tight">{s.title}</h2>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  {Array.isArray(s.body) ? (
                    <div className="space-y-5 text-[16px] md:text-[17px] leading-relaxed text-ink/85">
                      {s.body.map((p, j) => <p key={j}>{p}</p>)}
                    </div>
                  ) : (
                    <p className="text-[16px] md:text-[17px] leading-relaxed text-ink/85">{s.body}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {afterSections}

      {/* BULLETS / KEY POINTS */}
      {bullets.length > 0 && (
        <section className="bg-brand-deep text-white py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="section-num text-white/85">{t("services.common.coreBlock")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("services.common.coreTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-6 mt-16">
              {bullets.map((b, i) => (
                <div key={i} className="flex items-start gap-4 border-t border-white/15 pt-5">
                  <Check className="text-accent-lime shrink-0 mt-1" size={18}/>
                  <p className="text-[15px] leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FORMATS */}
      {formats.length > 0 && (
        <section className="bg-paper-2 py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="section-num">{t("services.common.formats")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("services.common.formatsTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 mt-16">
              {formats.map((f) => (
                <div key={f.title} className="bg-paper p-10 md:p-12">
                  <h3 className="display text-2xl md:text-3xl text-brand leading-tight">{f.title}</h3>
                  <p className="text-[15px] leading-relaxed mt-4 text-ink/75">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ClientsMarquee />

      {/* CTA */}
      <section className="bg-brand text-white py-24 md:py-32 grain relative">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="section-num text-white/85">{t("services.common.ctaEyebrow")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("services.common.ctaTitle")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid={`${testIdPrefix}-cta`} className="btn-pill bg-white text-brand hover:bg-paper-2">
              {t("common.cta.bookCall")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
      <GradientStrip height="h-2"/>
    </Layout>
  );
}
